import React from 'react';
import { AppLayout } from '@heroui-pro/react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getNavigationBackTarget, getNavigationTitle } from '../navigation';
import { NavbarSlotProvider } from '../navbarSlot';
import { DashboardNavbar } from './DashboardNavbar';
import { ControlCenterFeedback } from '@/pages/controlCenter';
import { DashboardSidebar } from './DashboardSidebar';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type AppShellProps = {
    children: React.ReactNode;
    showBack: boolean;
};

export const AppShell: React.FC<AppShellProps> = ({ children, showBack }) => {
    const { t } = useTranslation('components');
    const location = useLocation();
    const navigate = useNavigate();

    const title = React.useMemo(
        () => getNavigationTitle(location.pathname, t),
        [location.pathname, t],
    );

    const navigateTo = React.useCallback(
        (href: string) => navigate(href),
        [navigate],
    );
    const navigateBack = React.useCallback(() => {
        // A fixed target (e.g. manual import → /purchases) wins over history:
        // back always leaves to the parent section, never to a previous form
        // state, and saves nothing on the way out.
        const backTarget = getNavigationBackTarget(location.pathname);
        if (backTarget !== null) {
            navigate(backTarget);
            return;
        }
        navigate(-1);
    }, [location.pathname, navigate]);

    return (
        // The provider spans the navbar *and* the page, because the page sets
        // the title accessory and the navbar renders it.
        <NavbarSlotProvider>
            <div
                className="min-h-screen bg-background"
                {...getNodeTestIdProp('app-shell-root')}
                id="app-shell-root"
            >
                <AppLayout
                    data-testid="app-shell-layout"
                    id="app-shell-layout"
                    navbar={
                        <DashboardNavbar
                            onBack={navigateBack}
                            showBack={showBack}
                            title={title}
                        />
                    }
                    navigate={navigateTo}
                    sidebar={<DashboardSidebar pathname={location.pathname} />}
                    sidebarCollapsible="offcanvas"
                    sidebarVariant="floating"
                >
                    <div
                        className="flex w-full justify-start px-4 py-6 sm:pl-8"
                        {...getNodeTestIdProp('app-shell-main')}
                        id="app-shell-main"
                    >
                        <div
                            className="w-full max-w-7xl"
                            {...getNodeTestIdProp('app-shell-content')}
                            id="app-shell-content"
                        >
                            <ControlCenterFeedback />
                            {children}
                        </div>
                    </div>
                </AppLayout>
            </div>
        </NavbarSlotProvider>
    );
};
